import { Loader } from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import { INSURANCE_CARD_DISPLAY, INSURANCE_CARD_SIDES } from '@/constants/insuranceConstants'
import type { InsuranceCardSide } from '@/types/insuranceTypes'

type Props = {
  previews: Record<InsuranceCardSide, string | null>
  loading?: boolean
  emptyMessage?: string
  title?: string
  helperText?: string
  onImageClick?: (url: string) => void
  variant?: 'default' | 'compact'
}

export function InsuranceCardGallery({
  previews,
  loading = false,
  emptyMessage = 'No insurance card images are attached to this insurance.',
  title = 'Insurance card images',
  helperText,
  onImageClick,
  variant = 'default',
}: Props) {
  const hasPreviews = Boolean(previews.front || previews.back)

  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold text-gray-700">{title}</div>
      <div className="text-xs text-gray-500">
        {loading
          ? 'Loading card previews...'
          : hasPreviews
            ? helperText || 'Images attached to this insurance.'
            : emptyMessage}
      </div>
      <div className="flex flex-col gap-3 md:flex-row">
        {INSURANCE_CARD_SIDES.map((side) => {
          const previewUrl = previews[side]
          const display = INSURANCE_CARD_DISPLAY[side]

          return (
            <div
              key={side}
              className={`${variant === 'compact' ? 'min-w-[160px]' : 'min-w-[220px]'} flex-1 rounded-lg border border-gray-200 bg-white p-3 shadow-sm`}
            >
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">{display.label}</div>
                  <div className="text-xs text-gray-500">{display.helper}</div>
                </div>
                {previewUrl && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-purple-600 hover:underline"
                  >
                    View
                  </a>
                )}
              </div>
              <div
                className={`flex ${variant === 'compact' ? 'min-h-[100px]' : 'min-h-[150px]'} items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50 p-3 text-center ${previewUrl ? 'cursor-pointer' : ''}`}
                role={previewUrl ? 'button' : undefined}
                tabIndex={previewUrl ? 0 : undefined}
                onClick={() => {
                  if (previewUrl && onImageClick) {
                    onImageClick(previewUrl)
                  }
                }}
                onKeyDown={(event) => {
                  if (previewUrl && onImageClick && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault()
                    onImageClick(previewUrl)
                  }
                }}
              >
                {loading ? (
                  <Loader size="sm" />
                ) : previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={`${display.label} preview`}
                    className="max-h-48 w-full rounded object-contain"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-center text-sm text-gray-500">
                    <IconInfoCircle size={28} color="#9ca3af" />
                    <span>{display.placeholder}</span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
